import { LOCALES, LOCALE_NAMES, type Locale } from "@quality-field/shared";

import { useI18n } from "@/i18n/useI18n";
import { apiFetch } from "@/lib/api-client";

/**
 * FR / EN.
 *
 * ⚠ Each option is written in its OWN language — `Français`, never `French`. A language
 * picker you have to already read the interface to understand is no use to the person who
 * needs it.
 *
 * The choice applies immediately and is written to the person's account in the background
 * so it follows them to another machine. ⚠ A failed write is swallowed on purpose: the
 * language has already changed on screen, `localStorage` has it, and a red toast saying
 * "could not save your language" over a screen that is visibly already in French would be
 * both confusing and untrue.
 */
export function LanguageSwitch({ persist = true }: { persist?: boolean }) {
  const { locale, setLocale, t } = useI18n();

  const choose = (next: Locale) => {
    if (next === locale) return;
    setLocale(next);
    if (persist) {
      void apiFetch("/auth/language", { method: "POST", body: JSON.stringify({ language: next }) }).catch(
        () => {},
      );
    }
  };

  return (
    <div className="inline-flex rounded-lg border border-slate-200 p-0.5" role="group" aria-label={t("shell.language")}>
      {LOCALES.map((code) => (
        <button
          key={code}
          type="button"
          onClick={() => choose(code)}
          aria-pressed={locale === code}
          title={LOCALE_NAMES[code]}
          className={`rounded-md px-2 py-0.5 text-[11px] font-bold uppercase transition-colors ${
            locale === code ? "bg-slate-100 text-slate-700" : "text-slate-400 hover:text-slate-600"
          }`}
        >
          {code}
        </button>
      ))}
    </div>
  );
}
